import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  FaCamera,
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaMapMarkerAlt,
  FaServer,
  FaCheckCircle,
  FaExclamationCircle,
  FaArrowRight,
  FaShieldAlt,
  FaPlug,
  FaSignOutAlt,
  FaRegCircle,
} from "react-icons/fa";

const CITRIOT_LOGO = "/citriot_logo.webp";
const EMPIRE_LOGO = "/empirelogo.webp";

const INITIAL_FORM = {
  cameraName: "",
  location: "",
  ipAddress: "",
  port: "554",
  channel: "1",
  username: "",
  password: "",
};

const IP_REGEX =
  /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

const focusRing =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2";

const EmpireAddCamera = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [addingCamera, setAddingCamera] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("idle");

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setConnectionStatus("idle");

    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = () => {
    const e = {};

    if (!form.cameraName.trim()) e.cameraName = "Camera name is required";
    else if (form.cameraName.trim().length < 2)
      e.cameraName = "Enter a valid camera name";

    if (!form.location.trim()) e.location = "Location is required";

    if (!form.ipAddress.trim()) e.ipAddress = "IP address is required";
    else if (!IP_REGEX.test(form.ipAddress.trim()))
      e.ipAddress = "Enter a valid IPv4 address";

    if (!form.port.trim()) e.port = "Port is required";
    else {
      const port = Number(form.port);
      if (!Number.isInteger(port) || port < 1 || port > 65535)
        e.port = "Use a port from 1 to 65535";
    }

    if (!form.channel.trim()) e.channel = "Channel is required";
    else if (
      !Number.isInteger(Number(form.channel)) ||
      Number(form.channel) < 1
    )
      e.channel = "Enter a valid channel";

    if (!form.username.trim()) e.username = "Username is required";
    if (!form.password) e.password = "Password is required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      message.warning("Please complete all required camera details.");
      return;
    }

    setTestingConnection(true);
    setConnectionStatus("testing");

    try {
      /*
       * FRONTEND DEMO – replace with your backend API.
       *
       * await axios.post("/empire/test-camera", {
       *   ipAddress: form.ipAddress,
       *   port: Number(form.port),
       *   channel: Number(form.channel),
       *   username: form.username,
       *   password: form.password,
       * });
       */
      await new Promise((resolve) => setTimeout(resolve, 1200));

      setConnectionStatus("connected");
      message.success("Camera connection verified.");
    } catch (error) {
      console.error(error);
      setConnectionStatus("failed");
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to connect to the camera."
      );
    } finally {
      setTestingConnection(false);
    }
  };

  const handleAddCamera = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      message.warning("Please fix the highlighted fields.");
      return;
    }

    setAddingCamera(true);

    try {
      /* FRONTEND DEMO – replace with your backend API. */
      const camera = {
        id: `CAM-${Date.now()}`,
        cameraName: form.cameraName.trim(),
        location: form.location.trim(),
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
        channel: Number(form.channel),
        username: form.username.trim(),
        status: "online",
      };

      sessionStorage.setItem("empireCamera", JSON.stringify(camera));
      message.success("Camera added.");
      navigate("/empire/live-view", { replace: true });
    } catch (error) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add camera."
      );
    } finally {
      setAddingCamera(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const busy = testingConnection || addingCamera;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* ================= HEADER ================= */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-4">
            <img
              src={CITRIOT_LOGO}
              alt="Citriot"
              className="h-7 w-auto object-contain"
            />
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
                <img
                  src={EMPIRE_LOGO}
                  alt="Empire"
                  className="h-full w-full object-contain p-1.5"
                />
              </div>
              <div className="hidden leading-tight sm:block">
                <p className="text-sm font-bold text-slate-900">Empire</p>
                <p className="text-[11px] text-slate-400">
                  Camera Intelligence
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 md:flex">
              <span className="relative flex h-2 w-2">
                <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none" />
                <span className="relative h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-xs font-medium text-emerald-700">
                System online
              </span>
            </div>

            <div className="flex items-center gap-3 border-l border-slate-200 pl-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                  A
                </div>
                <div className="hidden leading-tight md:block">
                  <p className="text-sm font-semibold text-slate-900">Admin</p>
                  <p className="text-[11px] text-slate-400">Administrator</p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className={`flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-semibold text-slate-600 transition hover:bg-red-50 hover:text-red-600 ${focusRing}`}
              >
                <FaSignOutAlt size={13} />
                <span className="hidden sm:inline">Log out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ================= MAIN ================= */}
      <main className="mx-auto max-w-[1280px] px-5 py-8 sm:px-8 lg:py-12">
        <div className="mb-8">
          <p className="text-sm text-slate-400">
            Camera management <span className="mx-1">/</span> Hikvision
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            Add camera
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
            Connect a Hikvision camera to Empire for live monitoring and AI
            analytics.
          </p>
        </div>

        <form onSubmit={handleAddCamera} autoComplete="off">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
            {/* ===== LEFT: ONE PANEL, THREE STEPS ===== */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              {/* STEP 1 */}
              <Step
                number={1}
                title="Camera details"
                description="Name it and say where it's installed."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <Field label="Camera name" error={errors.cameraName}>
                    <Input
                      icon={<FaCamera />}
                      value={form.cameraName}
                      onChange={(v) => updateField("cameraName", v)}
                      placeholder="Dining area camera"
                      invalid={!!errors.cameraName}
                    />
                  </Field>

                  <Field label="Location" error={errors.location}>
                    <Input
                      icon={<FaMapMarkerAlt />}
                      value={form.location}
                      onChange={(v) => updateField("location", v)}
                      placeholder="Dining area"
                      invalid={!!errors.location}
                    />
                  </Field>
                </div>
              </Step>

              {/* STEP 2 */}
              <Step
                number={2}
                title="Network"
                description="Where Empire reaches the RTSP stream."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
                  <Field
                    label="IP address"
                    error={errors.ipAddress}
                    className="md:col-span-6"
                  >
                    <Input
                      icon={<FaServer />}
                      value={form.ipAddress}
                      onChange={(v) => updateField("ipAddress", v)}
                      placeholder="192.168.1.100"
                      invalid={!!errors.ipAddress}
                      mono
                      spellCheck={false}
                    />
                  </Field>

                  <Field
                    label="RTSP port"
                    error={errors.port}
                    className="md:col-span-3"
                  >
                    <Input
                      type="number"
                      min="1"
                      max="65535"
                      value={form.port}
                      onChange={(v) => updateField("port", v)}
                      invalid={!!errors.port}
                      mono
                    />
                  </Field>

                  <Field
                    label="Channel"
                    error={errors.channel}
                    className="md:col-span-3"
                  >
                    <Input
                      type="number"
                      min="1"
                      value={form.channel}
                      onChange={(v) => updateField("channel", v)}
                      invalid={!!errors.channel}
                      mono
                    />
                  </Field>
                </div>

                <div className="mt-5 rounded-xl bg-slate-50 px-4 py-3">
                  <p className="text-xs font-medium text-slate-500">
                    Stream URL
                  </p>
                  <p className="mt-1 break-all font-mono text-xs text-slate-700">
                    rtsp://{form.ipAddress || "camera-ip"}:
                    {form.port || "554"}/Streaming/Channels/
                    {form.channel || "1"}
                  </p>
                </div>
              </Step>

              {/* STEP 3 */}
              <Step
                number={3}
                title="Camera credentials"
                description="The Hikvision device login, not your Empire account."
                last
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <Field label="Username" error={errors.username}>
                    <Input
                      icon={<FaUser />}
                      value={form.username}
                      onChange={(v) => updateField("username", v)}
                      placeholder="admin"
                      invalid={!!errors.username}
                      spellCheck={false}
                      autoComplete="new-password"
                      noManager
                    />
                  </Field>

                  <Field label="Password" error={errors.password}>
                    <Input
                      icon={<FaLock />}
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(v) => updateField("password", v)}
                      placeholder="Camera password"
                      invalid={!!errors.password}
                      autoComplete="new-password"
                      noManager
                      trailing={
                        <button
                          type="button"
                          onClick={() => setShowPassword((p) => !p)}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          className={`flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:text-slate-700 ${focusRing}`}
                        >
                          {showPassword ? (
                            <FaEyeSlash size={14} />
                          ) : (
                            <FaEye size={14} />
                          )}
                        </button>
                      }
                    />
                  </Field>
                </div>

                <p className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                  <FaShieldAlt className="text-emerald-500" />
                  Credentials are used only to connect to this camera.
                </p>
              </Step>

              {/* ACTION BAR */}
              <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/70 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">
                  Test the connection before adding the camera.
                </p>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={busy}
                    className={`flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
                  >
                    {testingConnection ? (
                      <>
                        <Spinner />
                        Testing
                      </>
                    ) : (
                      <>
                        <FaPlug className="text-xs" />
                        Test connection
                      </>
                    )}
                  </button>

                  <button
                    type="submit"
                    disabled={busy}
                    className={`flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
                  >
                    {addingCamera ? (
                      <>
                        <Spinner white />
                        Adding camera
                      </>
                    ) : (
                      <>
                        Add camera
                        <FaArrowRight className="text-xs" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ===== RIGHT: STATUS + SUMMARY ===== */}
            <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
              <ConnectionCard status={connectionStatus} />

              <div className="relative overflow-hidden rounded-2xl bg-slate-900 p-6 text-white">
                <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-blue-500/20 blur-3xl" />

                <div className="relative">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                      <FaCamera className="text-sm" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {form.cameraName.trim() || "New camera"}
                      </p>
                      <p className="text-xs text-white/50">Hikvision</p>
                    </div>
                  </div>

                  <div className="mt-5">
                    <InfoRow label="Protocol" value="RTSP" />
                    <InfoRow label="IP address" value={form.ipAddress || "Not set"} />
                    <InfoRow label="Port" value={form.port || "554"} />
                    <InfoRow label="Channel" value={form.channel || "1"} />
                    <InfoRow label="Location" value={form.location || "Not set"} />
                  </div>

                  <p className="mt-4 text-xs leading-relaxed text-white/45">
                    Once added, this camera is available for Empire live
                    monitoring and AI use cases.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </form>
      </main>
    </div>
  );
};

/* =========================================================
   COMPONENTS
========================================================= */

const Step = ({ number, title, description, children, last = false }) => (
  <section className={`px-6 py-6 ${last ? "" : "border-b border-slate-100"}`}>
    <div className="mb-5 flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
        {number}
      </span>
      <div>
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        <p className="mt-0.5 text-sm text-slate-500">{description}</p>
      </div>
    </div>
    <div className="sm:pl-10">{children}</div>
  </section>
);

const Field = ({ label, error, className = "", children }) => (
  <div className={className}>
    <label className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
    </label>
    {children}
    {error && (
      <div
        role="alert"
        className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600"
      >
        <FaExclamationCircle className="text-[10px]" />
        <span>{error}</span>
      </div>
    )}
  </div>
);

const Input = ({
  icon,
  trailing,
  invalid,
  mono,
  noManager,
  onChange,
  type = "text",
  ...rest
}) => {
  const managerProps = noManager
    ? {
        "data-lpignore": "true",
        "data-1p-ignore": "true",
        "data-form-type": "other",
      }
    : {};

  return (
    <div className="relative">
      {icon && (
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">
          {icon}
        </span>
      )}

      <input
        type={type}
        onChange={(e) => onChange(e.target.value)}
        className={`h-11 w-full rounded-xl border bg-white text-sm text-slate-800 outline-none transition placeholder:text-slate-300 ${
          icon ? "pl-10" : "pl-4"
        } ${trailing ? "pr-11" : "pr-4"} ${
          mono ? "font-mono text-[13px]" : ""
        } ${
          invalid
            ? "border-red-300 ring-4 ring-red-50"
            : "border-slate-200 hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
        }`}
        aria-invalid={invalid || undefined}
        {...managerProps}
        {...rest}
      />

      {trailing && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {trailing}
        </div>
      )}
    </div>
  );
};

const Spinner = ({ white = false }) => (
  <span
    className={`inline-block h-4 w-4 animate-spin rounded-full border-2 motion-reduce:animate-none ${
      white
        ? "border-white/30 border-t-white"
        : "border-blue-200 border-t-blue-600"
    }`}
  />
);

const STATUS = {
  idle: {
    icon: <FaRegCircle />,
    title: "Not tested",
    description: "Enter the camera details, then test the connection.",
    iconStyle: "bg-slate-100 text-slate-500",
    titleStyle: "text-slate-700",
  },
  testing: {
    icon: <Spinner />,
    title: "Testing connection",
    description: "Checking the camera. This takes a few seconds.",
    iconStyle: "bg-amber-50 text-amber-600",
    titleStyle: "text-amber-700",
  },
  connected: {
    icon: <FaCheckCircle />,
    title: "Camera connected",
    description: "The camera responded. You can add it now.",
    iconStyle: "bg-emerald-50 text-emerald-600",
    titleStyle: "text-emerald-700",
  },
  failed: {
    icon: <FaExclamationCircle />,
    title: "Connection failed",
    description: "Check the IP address, port, and credentials, then retry.",
    iconStyle: "bg-red-50 text-red-600",
    titleStyle: "text-red-700",
  },
};

const ConnectionCard = ({ status }) => {
  const s = STATUS[status];

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      aria-live="polite"
    >
      <p className="mb-4 text-sm font-bold text-slate-900">
        Connection status
      </p>

      <div className="flex gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${s.iconStyle}`}
        >
          {s.icon}
        </div>
        <div>
          <p className={`text-sm font-semibold ${s.titleStyle}`}>{s.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            {s.description}
          </p>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/10 py-2.5 last:border-0">
    <span className="text-xs text-white/50">{label}</span>
    <span className="max-w-[170px] truncate font-mono text-xs font-medium text-white/85">
      {value}
    </span>
  </div>
);

export default EmpireAddCamera;